import React, { ReactNode, useState } from 'react';
import {
	AlertBox,
	Breadcrumbs,
	BreadcrumbsText,
	Button,
	Heading,
	Link
} from 'neon-design-system';
import {Box, createStyles, makeStyles} from '@material-ui/core';
import { AlertMessage } from '../../shared/types';
import ManageTemplateDialog from './ManageTemplateDialog';

export enum ManageTemplateInstance {
	Category = 'category',
	PromotionalContent = 'promotional content',
	PromotionPage = 'promotion page',
	Subscription = 'subscribe',
	Tag = 'tag',
	User = 'user',
	Article = 'article',
	Desk = 'desk',
	Template = 'template',
	Channel = 'Channel',
	Topic = 'Topic',
	ChannelSubscriber = 'subscriber',
	ChannelSubscribers= 'subscribers'
}

const useStyles = makeStyles(theme => createStyles({
	editsDiv: {
		width: 384
	},
	actionsWrapper: {
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(2),
		width: 384
	},
	textField: {
		color: theme.palette.grey[400]
	},
	alertDiv: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	btnSubmit: {
		marginRight: theme.spacing(1)
	}
}));

export interface WithManageTemplateProps extends ManageTemplateHeaderProps, ManageTemplateDialogBase {
	children: ReactNode;
	formMessage: AlertMessage;
	formBusy: boolean;
	onCancel: () => void;
	onSubmit: (ev: any) => void;
	showSubmitButton?: boolean;
	showCancelButton?: boolean;
	onAlertBoxClose: () => void;
	helperContent?: JSX.Element;
	outOfFormContent?: JSX.Element;
	templateCreateButtonLabel?: string;
	templateUpdateButtonLabel?: string;
	templateDeleteButtonLabel?: string;
	maxContentWidth?: string | number;
	templateDeleteDialogTitle?: string;
	isReadOnly?: boolean;
}

const ManageTemplate = (props: WithManageTemplateProps): JSX.Element => {
	const {
		isReadOnly = false,
		children,
		isNew,
		templateInstance,
		pathArea,
		returnUrl,
		formMessage,
		onAlertBoxClose,
		onSubmit,
		onCancel,
		dialogMessage,
		helperContent,
		onDelete = () => { },
		showDelete = true,
		deleteDialogBtnDisabled = false,
		outOfFormContent,
		templateCreateButtonLabel = `Add new ${templateInstance}`,
		templateUpdateButtonLabel = 'Update',
		templateDeleteButtonLabel = `Delete ${templateInstance}`,
		breadcrumbOverride,
		maxContentWidth,
		templateDeleteDialogTitle = 'Delete',
		showSubmitButton = true,
		showCancelButton = true,
		titleOverride
	} = props;
	const classes = useStyles();
	const { openDialog, dialogOpenState, closeDialog } = useDialog();

	return <Box style={{ maxWidth: maxContentWidth }}>
		<ManageTemplateHeader
			isNew={isNew}
			templateInstance={templateInstance}
			pathArea={pathArea}
			returnUrl={returnUrl}
			breadcrumbOverride={breadcrumbOverride}
			titleOverride={titleOverride}
		/>
		<div className={classes.alertDiv}>
			<AlertBox
				id="manage-template-alert"
				visible={!!formMessage?.message}
				children={formMessage?.message}
				styleName={formMessage?.style}
				onClose={onAlertBoxClose}
			/>
		</div>
		{helperContent}
		<form
			autoComplete="off"
			onSubmit={onSubmit}
			onReset={onCancel}>
			<div className={classes.editsDiv}>
				{children}
			</div>
		</form>
		{outOfFormContent && <div className={classes.editsDiv}>{outOfFormContent}</div>}
		<div className={classes.actionsWrapper}>
			<span>
				{
					showSubmitButton && (
						<Button
							busy={props.formBusy}
							id={`${templateInstance}-submit-button`}
							colorType="primary"
							onClick={onSubmit}
							className={classes.btnSubmit}
							disabled={isReadOnly}
						>
							{isNew ? templateCreateButtonLabel : templateUpdateButtonLabel}
						</Button>
					)
				}
				{
					showCancelButton && (
						<Button
							id={`${templateInstance}-cancel-button`}
							colorType="grey"
							onClick={onCancel}

						>
							Cancel
						</Button>
					)
				}
			</span>
			{!isNew && showDelete && <Button
				id={`${templateInstance}-delete-button`}
				colorType="error"
				onClick={(): void => {
					openDialog();
				}}>
				{templateDeleteButtonLabel}
			</Button>}
		</div>
		{!isReadOnly && showDelete && <ManageTemplateDialog
			showDelete={showDelete}
			submitBtnDisabled={deleteDialogBtnDisabled}
			onDelete={onDelete}
			submitBtnColorType="error"
			dialogMessage={dialogMessage}
			title={`${templateDeleteDialogTitle} this ${templateInstance}?`}
			submitBtnText={templateDeleteButtonLabel}
			dialogOpenState={dialogOpenState}
			openDialog={openDialog}
			closeDialog={closeDialog} />}
	</Box>;
};

interface ManageTemplateHeaderProps {
	isNew: boolean;
	templateInstance: ManageTemplateInstance;
	breadcrumbOverride?: JSX.Element;
	pathArea: string;
	returnUrl: string;
	titleOverride?: string;
}

const useHeaderStyles = makeStyles(theme => createStyles({
	mainDiv: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	heading1: {
		marginBottom: theme.spacing(2),
		width: '100%'
	},
	heading2: {
		marginTop: theme.spacing(4)
	},
	divider: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(4)
	}
}));

export const ManageTemplateHeader = (props: ManageTemplateHeaderProps): JSX.Element => {
	const classes = useHeaderStyles();
	const { isNew, templateInstance, pathArea, returnUrl, breadcrumbOverride, titleOverride } = props;
	const title = isNew ? `Add ${templateInstance === ManageTemplateInstance.PromotionalContent || templateInstance === ManageTemplateInstance.ChannelSubscribers ? '' : 'a '}new ${templateInstance}` : `Edit ${templateInstance === ManageTemplateInstance.PromotionalContent ? '' : 'a '}${templateInstance}`;
	const pathOperation = isNew ? `New ${templateInstance}` : `Edit ${templateInstance}`;

	return <div className={classes.mainDiv}>
		<div className={classes.heading1}>
			{breadcrumbOverride
				? breadcrumbOverride
				: <Breadcrumbs>
					<Link to={returnUrl}>{pathArea}</Link>
					<BreadcrumbsText>{pathOperation}</BreadcrumbsText>
				</Breadcrumbs>
			}
		</div>
		<div>
			<Heading styleName="title1" className={classes.heading2}>{titleOverride || title}</Heading>
		</div>
	</div>
};

export interface ManageTemplateDialogBase {
	dialogMessage?: ReactNode;
	onDelete?: () => void;
	showDelete?: boolean;
	deleteDialogBtnDisabled?: boolean;
}

export interface ManageTemplateDialog {
	dialogOpenState: boolean;
	openDialog: () => void;
	closeDialog: () => void;
}

const useDialog = (): ManageTemplateDialog => {
	const [dialogOpenState, setDialogOpenState] = useState<boolean>(false);

	const openDialog = (): void => setDialogOpenState(true);
	const closeDialog = (): void => setDialogOpenState(false);

	return { dialogOpenState, openDialog, closeDialog };
}

export default ManageTemplate;
